import CachedIcon from '@mui/icons-material/Cached';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  type AlertColor,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import type { PersonRoleItem } from '@shared/types/rbac';
import { useState } from 'react';
import {
  useBustRbacCacheMutation,
  useGetAdminPersonRolesQuery,
  useListOrganizationsQuery,
} from '../../store/api/apiSlice';
import { readCronTriggerError, useListCronsQuery, useTriggerCronMutation } from './api/cronApi';
import CronJobCard from './components/CronJobCard';

const CRON_STATUS_POLL_INTERVAL_MS = 15_000;

export default function CronJobsPage() {
  const { data, isLoading, isFetching, error, refetch } = useListCronsQuery(undefined, {
    pollingInterval: CRON_STATUS_POLL_INTERVAL_MS,
  });
  const [triggerCron, { isLoading: isTriggeringCalendarSync }] = useTriggerCronMutation();
  const [bustRbacCache, { isLoading: isBustingCache }] = useBustRbacCacheMutation();

  // RBAC cache bust state
  const [emailSearch, setEmailSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPerson, setSelectedPerson] = useState<PersonRoleItem | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [cacheResult, setCacheResult] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [calendarLookbackDays, setCalendarLookbackDays] = useState('90');
  const [calendarSyncResult, setCalendarSyncResult] = useState<{
    type: AlertColor;
    message: string;
  } | null>(null);

  // Fetch orgs for dropdown
  const { data: orgs = [], isLoading: isLoadingOrgs } = useListOrganizationsQuery({ limit: 100 });

  // Search people by email
  const { data: searchResults, isFetching: isSearching } = useGetAdminPersonRolesQuery(
    { q: searchTerm, per: 10 },
    { skip: !searchTerm || searchTerm.length < 2 }
  );

  const crons = data?.crons ?? [];
  const people = searchResults?.items ?? [];

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchTerm(emailSearch.trim());
  };

  const handleSelectPerson = (person: PersonRoleItem) => {
    setSelectedPerson(person);
    setSelectedOrgId(person.org_id || '');
    setEmailSearch('');
    setSearchTerm('');
  };

  const handleClearSelection = () => {
    setSelectedPerson(null);
    setSelectedOrgId('');
  };

  const handleBustCache = async () => {
    if (!selectedPerson) return;
    setCacheResult(null);
    try {
      await bustRbacCache({
        personId: selectedPerson.person_id,
        orgId: selectedOrgId || undefined,
      }).unwrap();
      setCacheResult({
        type: 'success',
        message: `Cache busted for ${selectedPerson.email}`,
      });
      // Clear selection after success
      setSelectedPerson(null);
      setSelectedOrgId('');
    } catch (err: any) {
      setCacheResult({
        type: 'error',
        message: err?.data?.error_description || err?.message || 'Failed to bust cache',
      });
    }
  };

  const handleRunFullCalendarSync = async () => {
    const lookbackDays = Number.parseInt(calendarLookbackDays.trim(), 10);
    if (!Number.isInteger(lookbackDays) || lookbackDays <= 0) {
      setCalendarSyncResult({
        type: 'error',
        message: 'Lookback days must be a positive integer.',
      });
      return;
    }

    setCalendarSyncResult(null);
    try {
      await triggerCron({
        name: 'scheduling-cal-sync-full',
        event_args: {
          force_full: true,
          lookback_days: lookbackDays,
        },
      }).unwrap();
      setCalendarSyncResult({
        type: 'success',
        message: `Triggered full calendar sync (${lookbackDays}-day lookback).`,
      });
    } catch (err: unknown) {
      const details = readCronTriggerError(err);
      if (details.error === 'already_running' && details.active_run) {
        setCalendarSyncResult({
          type: 'info',
          message: `Full calendar sync is already ${details.active_run.status} from a ${details.active_run.trigger_type} trigger.`,
        });
        return;
      }

      setCalendarSyncResult({
        type: 'error',
        message: details.error_description || 'Failed to trigger full calendar sync.',
      });
    }
  };

  return (
    <Box sx={{ maxWidth: 900, margin: '0 auto', padding: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="h4" component="h1">
          Cron Jobs
        </Typography>
        {(isLoading || isFetching) && <CircularProgress size={20} />}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View and manage scheduled background jobs. Trigger jobs manually or view execution history.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load cron jobs. {(error as any)?.data?.error_description || 'Please try again.'}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
          disabled={isFetching}
        >
          Refresh
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : crons.length === 0 ? (
        <Alert severity="info">No cron jobs configured.</Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {crons.map((cron) => (
            <CronJobCard key={cron.name} cron={cron} />
          ))}
        </Box>
      )}

      <Divider sx={{ my: 4 }} />

      {/* System Maintenance Section */}
      <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
        System Maintenance
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manual system maintenance tasks. Use these after deployments or migrations that affect user
        roles.
      </Typography>

      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CachedIcon color="primary" />
            <Typography variant="h6">RBAC Cache Bust</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Clear the RBAC cache for a specific user to immediately apply role changes. This is
            useful after deployments or migrations that modify roles. The cache TTL is 10 minutes,
            so users will eventually get correct roles automatically.
          </Typography>

          {cacheResult && (
            <Alert severity={cacheResult.type} sx={{ mb: 2 }} onClose={() => setCacheResult(null)}>
              {cacheResult.message}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Selected person display */}
            {selectedPerson ? (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {selectedPerson.email}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      ID: {selectedPerson.person_id}
                    </Typography>
                    {selectedPerson.org_name && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        Org: {selectedPerson.org_name}
                      </Typography>
                    )}
                    <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {selectedPerson.roles.length === 0 ? (
                        <Chip size="small" label="No roles" color="default" />
                      ) : (
                        selectedPerson.roles.map((role) => (
                          <Chip
                            key={`${role.role_name}-${role.org_id || 'global'}`}
                            size="small"
                            label={role.role_name}
                            color="primary"
                            variant="outlined"
                          />
                        ))
                      )}
                    </Box>
                  </Box>
                  <IconButton size="small" onClick={handleClearSelection}>
                    <ClearIcon />
                  </IconButton>
                </Box>
              </Paper>
            ) : (
              /* Email search */
              <Box component="form" onSubmit={handleSearchSubmit}>
                <TextField
                  label="Search by email"
                  placeholder="Enter email to search..."
                  size="small"
                  value={emailSearch}
                  onChange={(e) => setEmailSearch(e.target.value)}
                  helperText="Type at least 2 characters and press Enter to search"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: isSearching ? (
                      <InputAdornment position="end">
                        <CircularProgress size={16} />
                      </InputAdornment>
                    ) : null,
                  }}
                />
                {/* Search results */}
                {searchTerm && people.length > 0 && (
                  <Paper variant="outlined" sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
                    <List dense disablePadding>
                      {people.map((person) => (
                        <ListItemButton
                          key={person.person_id}
                          onClick={() => handleSelectPerson(person)}
                        >
                          <ListItemText
                            primary={person.email}
                            secondary={
                              <>
                                {person.org_name || 'No org'}
                                {person.roles.length > 0 &&
                                  ` • ${person.roles.map((r) => r.role_name).join(', ')}`}
                              </>
                            }
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Paper>
                )}
                {searchTerm && people.length === 0 && !isSearching && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No users found for "{searchTerm}"
                  </Typography>
                )}
              </Box>
            )}

            {/* Organization dropdown */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Organization (for cache key)
              </Typography>
              <Select
                size="small"
                fullWidth
                displayEmpty
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
                disabled={isLoadingOrgs}
              >
                <MenuItem value="">
                  <em>Global (no org context)</em>
                </MenuItem>
                {orgs.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary">
                Usually should match the user's organization. Leave as "Global" for global roles.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="warning"
                startIcon={
                  isBustingCache ? <CircularProgress size={16} color="inherit" /> : <CachedIcon />
                }
                onClick={handleBustCache}
                disabled={isBustingCache || !selectedPerson}
              >
                {isBustingCache ? 'Busting Cache...' : 'Bust Cache'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Global Calendar Sync
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Trigger a global full scheduling calendar sync with a configurable lookback window.
          </Typography>

          {calendarSyncResult && (
            <Alert
              severity={calendarSyncResult.type}
              sx={{ mb: 2 }}
              onClose={() => setCalendarSyncResult(null)}
            >
              {calendarSyncResult.message}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Lookback days"
              size="small"
              type="number"
              value={calendarLookbackDays}
              onChange={(e) => setCalendarLookbackDays(e.target.value)}
              inputProps={{ min: 1 }}
            />
            <Button
              variant="contained"
              onClick={handleRunFullCalendarSync}
              disabled={isTriggeringCalendarSync}
            >
              {isTriggeringCalendarSync ? 'Triggering...' : 'Run Global Calendar Sync'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
